import {
  Component,
  Output,
  EventEmitter,
  Injector,
  OnInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { FormBuilderService } from '../../../../core/services/form-builder.service';

@Component({
  selector: 'irembogov-fb-fullscreen-editor',
  templateUrl: './fb-fullscreen-editor.component.html',
  styleUrls: ['./fb-fullscreen-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FbFullscreenEditorComponent implements OnInit {
  @Output() closed = new EventEmitter<void>();

  private readonly THEME_KEY = 'fb-fs-editor-theme';
  /** Monaco theme: 'vs-dark' (dark) or 'vs' (light). Defaults to dark. */
  theme: 'vs-dark' | 'vs' = 'vs-dark';

  editorOptions = {
    theme: this.theme,
    language: 'json',
    minimap: { enabled: true },
    lineNumbers: 'on' as const,
    folding: true,
    automaticLayout: true,
    wordWrap: 'on' as const,
    formatOnPaste: true,
    formatOnType: true,
    scrollBeyondLastLine: false,
  };

  code = '';
  hasError = false;
  errorMessage = '';

  private formBuilderService: FormBuilderService;

  constructor(private injector: Injector, private cdr: ChangeDetectorRef) {
    this.formBuilderService = this.injector.get(FormBuilderService);
  }

  ngOnInit(): void {
    this.restoreTheme();
    this.loadCurrentFields();
  }

  get isDark(): boolean {
    return this.theme === 'vs-dark';
  }

  toggleTheme(): void {
    this.theme = this.theme === 'vs-dark' ? 'vs' : 'vs-dark';
    this.editorOptions = { ...this.editorOptions, theme: this.theme };
    try {
      localStorage.setItem(this.THEME_KEY, this.theme);
    } catch {
      /* localStorage unavailable — ignore */
    }
    // Apply live without recreating the editor when Monaco is loaded.
    const monaco = (window as unknown as { monaco?: any }).monaco;
    monaco?.editor?.setTheme?.(this.theme);
    this.cdr.markForCheck();
  }

  private restoreTheme(): void {
    try {
      const stored = localStorage.getItem(this.THEME_KEY);
      this.theme = stored === 'vs' ? 'vs' : 'vs-dark';
    } catch {
      this.theme = 'vs-dark';
    }
    this.editorOptions = { ...this.editorOptions, theme: this.theme };
  }

  onCodeChange(code: string): void {
    this.code = code;
    this.validateJson();
  }

  onFormat(): void {
    try {
      const parsed = JSON.parse(this.code);
      this.code = JSON.stringify(parsed, null, 2);
      this.hasError = false;
      this.errorMessage = '';
    } catch (e) {
      // Can't format invalid JSON
    }
    this.cdr.markForCheck();
  }

  onSave(): void {
    try {
      const parsed = JSON.parse(this.code);
      const fields: FormlyFieldConfig[] = parsed.fields || parsed;
      this.formBuilderService.formlyFieldsSubject$.next({
        fields: Array.isArray(fields) ? fields : [fields],
      });
      this.formBuilderService.refreshFormlyForm(true, true);
      this.closed.emit();
    } catch (e) {
      this.hasError = true;
      this.errorMessage = `Cannot save: ${(e as Error).message}`;
      this.cdr.markForCheck();
    }
  }

  onClose(): void {
    this.closed.emit();
  }

  onKeyDown(event: KeyboardEvent): void {
    const isCtrlOrMeta = event.ctrlKey || event.metaKey;
    if (isCtrlOrMeta && event.key === 's') {
      event.preventDefault();
      this.onSave();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.onClose();
    }
  }

  private loadCurrentFields(): void {
    const data = this.formBuilderService.formlyFieldsSubject$.value;
    this.code = JSON.stringify({ fields: data.fields }, null, 2);
    this.cdr.markForCheck();
  }

  private validateJson(): void {
    try {
      JSON.parse(this.code);
      this.hasError = false;
      this.errorMessage = '';
    } catch (e) {
      this.hasError = true;
      this.errorMessage = (e as Error).message;
    }
  }
}
